/**
 * Display rules. The two that matter are enforced here rather than at each
 * call site, so no screen can accidentally break them:
 *
 *   1. A null cost is NOT $0.00. Tokens were spent; no rate is on file. It
 *      renders as an em dash and is excluded from totals.
 *   2. Free is NOT absent. A stage that ran for nothing (a digital PDF's text
 *      layer, Tesseract) shows "$0.00 free"; a stage with no record at all is
 *      reported as unrecorded.
 */

export const EM_DASH = "—";

const tokens = new Intl.NumberFormat("en-US");

export const fmtTokens = (n: number | null | undefined): string =>
  n === null || n === undefined ? EM_DASH : tokens.format(n);

/** Costs run to fractions of a cent, so the usual 2dp money format hides them. */
export function fmtCost(usd: number | null | undefined): string {
  if (usd === null || usd === undefined) return EM_DASH;
  if (usd === 0) return "$0.00";
  if (usd < 0.01) return `$${usd.toFixed(6)}`;
  return `$${usd.toFixed(4)}`;
}

/**
 * The same cost at a fixed width, for dense places like a card or a stat strip.
 *
 * `fmtCost` varies its precision with the value, which is right on a detail
 * page and wrong in a grid: "$0.008979" beside "$0.0130" reads as two
 * different kinds of number, and six decimal places is precision this figure
 * does not have — it is a token count multiplied by a published rate.
 *
 * The two invariants from the top of this file still hold. Null stays an em
 * dash and is never $0.00. And a value too small for four places renders as
 * "<$0.0001", never as "$0.00", so a run that cost almost nothing is still
 * distinguishable from one that was genuinely free.
 */
export function fmtCostCompact(usd: number | null | undefined): string {
  if (usd === null || usd === undefined) return EM_DASH;
  if (usd === 0) return "$0.00";
  if (usd < 0.0001) return "<$0.0001";
  return `$${usd.toFixed(4)}`;
}

/** Confidence arrives as 0-1 from OCR and 0-100 from the agent. Normalise to 0-100. */
export function toPercent(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "string" ? Number(v) : v;
  if (!Number.isFinite(n)) return null;
  return n <= 1 ? n * 100 : n;
}

export type ConfidenceBand = "good" | "warning" | "serious" | "critical" | "none";

/**
 * Bands follow the platform's own confidence gate rather than round numbers,
 * so the badge agrees with the routing decision a reviewer sees next to it.
 */
export function confidenceBand(pct: number | null): ConfidenceBand {
  if (pct === null) return "none";
  if (pct >= 90) return "good";
  if (pct >= 75) return "warning";
  if (pct >= 50) return "serious";
  return "critical";
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return EM_DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return EM_DASH;
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return EM_DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return EM_DASH;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

/** trade_licence -> Trade Licence */
export const humanise = (s: string | null | undefined): string =>
  !s ? EM_DASH : s.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const fmtMs = (ms: number | null | undefined): string =>
  ms === null || ms === undefined ? EM_DASH : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;

/** Renders an arbitrary extracted value: arrays as lines, null as an em dash. */
export function fmtValue(v: unknown): string {
  if (v === null || v === undefined || v === "") return EM_DASH;
  if (Array.isArray(v)) return v.length ? v.map((x) => String(x)).join("\n") : EM_DASH;
  if (typeof v === "object") return JSON.stringify(v, null, 2);
  return String(v);
}

/** True when a stage cost nothing and that zero is a verified fact. */
export const isVerifiedFree = (s: { cost_verified: boolean; total_tokens: number }): boolean =>
  s.cost_verified && s.total_tokens === 0;
